import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function isSchemaError(error) {
  if (!error) return false;

  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }

  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some((pattern) => pattern?.test(error?.message));
  }

  return false;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase?.auth?.getSession();
        if (error) {
          if (isSchemaError(error)) {
            console.error('Schema error during session check:', error?.message);
            throw error;
          }
        }
        setUser(session?.user ?? null);
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const signUp = async (email, password, fullName) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during sign up:', error?.message);
          throw error;
        } else {
          throw new Error(error.message);
        }
      }

      return { user: data?.user, error: null };
    } catch (error) {
      return { user: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during sign in:', error?.message);
          throw error;
        } else {
          throw new Error(error.message);
        }
      }

      return { user: data?.user, error: null };
    } catch (error) {
      return { user: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during sign out:', error?.message);
          throw error;
        } else {
          throw new Error(error.message);
        }
      }
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`,
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during password reset:', error?.message);
          throw error;
        } else {
          throw new Error(error.message);
        }
      }

      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const updateUserMetadata = async (metadata) => {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        data: metadata,
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during metadata update:', error?.message);
          throw error;
        } else {
          throw new Error(error.message);
        }
      }

      return { user: data?.user, error: null };
    } catch (error) {
      return { user: null, error };
    }
  };

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateUserMetadata,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
